import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Question } from '../../interfaces/career-setup/question.interface';
import { ApiResponse } from '../../interfaces/ApiResponse';
import { PageResponse } from '../../interfaces/page-response.interface';
import { environment } from '../../../../environments/environment.dev';
import { convertToHttpParams, CommonSearchParam } from '../../interfaces/common-search-param.interface';
import { QuestionService } from './question.service';

@Injectable({
  providedIn: 'root'
})
export class QuestionApiService extends QuestionService {
  private http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiBaseUrl}v1/question`;

  constructor() {
    super();
  }

  override findAll(params: CommonSearchParam): Observable<PageResponse<Question>> {
    const httpParams = convertToHttpParams(params);
    return this.wrapRequest(
      this.http.get<ApiResponse<PageResponse<Question>>>(this.baseUrl, {
        headers: this.headers,
        params: httpParams
      }).pipe(
        map(res => res?.response || { content: [], page: { number: 0, size: 25, totalElements: 0, totalPages: 0 } })
      ),
      false,
      false
    );
  }

  override find(id: string): Observable<Question> {
    return this.wrapRequest(
      this.http.get<ApiResponse<Question>>(`${this.baseUrl}/${id}`, { headers: this.headers }).pipe(
        map(res => res?.response)
      ),
      false,
      false
    );
  }

  override create(data: Partial<Question>): Observable<Question> {
    return this.wrapRequest(
      this.http.post<ApiResponse<Question>>(this.baseUrl, data, { headers: this.headers }).pipe(
        map(res => res?.response)
      ),
      false,
      false
    );
  }

  override update(id: string, data: Partial<Question>): Observable<Question> {
    return this.wrapRequest(
      this.http.put<ApiResponse<Question>>(`${this.baseUrl}/${id}`, data, { headers: this.headers }).pipe(
        map(res => res?.response)
      ),
      false,
      false
    );
  }

  override updateStatus(id: string, isActive: boolean): Observable<Question> {
    return this.wrapRequest(
      this.http.patch<ApiResponse<Question>>(`${this.baseUrl}/${id}/status`, null, {
        headers: this.headers,
        params: { isActive: isActive.toString() }
      }).pipe(
        map(res => res?.response)
      ),
      false,
      false
    );
  }

  override delete(id: string): Observable<void> {
    return this.wrapRequest(
      this.http.delete<ApiResponse<void>>(`${this.baseUrl}/${id}`, { headers: this.headers }).pipe(
        map(() => undefined)
      ),
      false,
      false
    );
  }
}
